import { useEffect, useState } from "react";
import {
  ClockCounterClockwiseIcon as ClockCounterClockwise,
  FolderIcon as Folder,
  FileTextIcon as FileText,
  SparkleIcon as Sparkle,
  EyeIcon as Eye,
  LinkSimpleIcon as LinkSimple,
  CopyIcon as Copy,
  PaperPlaneTiltIcon as PaperPlaneTilt,
  EnvelopeIcon as Envelope,
  FilePdfIcon as FilePdf,
} from "@phosphor-icons/react";

const EVENT_LABELS = {
  workspace_created: { label: "Workspace created", icon: Folder },
  deliverable_generated: { label: "Deliverable generated", icon: FileText },
  briefing_generated: { label: "Client briefing generated", icon: Sparkle },
  share_link_viewed: { label: "Shared link opened", icon: Eye },
  share_link_unfurled: { label: "Link unfurled (preview)", icon: LinkSimple },
  share_link_copied: { label: "Share link copied", icon: Copy },
  email_opened_in_client: { label: "Opened in email client", icon: PaperPlaneTilt },
  email_copied: { label: "Email body copied", icon: Envelope },
  combined_pdf_exported: { label: "Combined PDF exported", icon: FilePdf },
  single_deliverable_pdf_exported: { label: "Deliverable PDF exported", icon: FilePdf },
};

function summarizeMetadata(m) {
  // Keep it compact: name=value pairs, truncated and joined with ·.
  const out = [];
  for (const [k, v] of Object.entries(m)) {
    if (v === null || v === undefined || v === "") continue;
    const val = typeof v === "string" ? v.slice(0, 40) : v;
    out.push(`${k}: ${val}`);
    if (out.length >= 3) break;
  }
  return out.join(" · ");
}

function formatRelative(fromMs, nowMs) {
  if (!fromMs) return "—";
  const diff = Math.max(0, Math.round((nowMs - fromMs) / 1000));
  if (diff < 5) return "just now";
  if (diff < 60) return `${diff}s ago`;
  const m = Math.round(diff / 60);
  if (m < 60) return `${m}m ago`;
  const h = Math.round(m / 60);
  return `${h}h ago`;
}

function LiveIndicator({ lastFetchedAt }) {
  // Tick once a second so "Xs ago" stays current — cheap setState, only
  // mounted while the engagement log is expanded.
  const [, setTick] = useState(0);
  useEffect(() => {
    const t = setInterval(() => setTick((n) => n + 1), 1000);
    return () => clearInterval(t);
  }, []);
  if (!lastFetchedAt) return null;
  return (
    <span
      className="ml-mono text-[10px] uppercase tracking-widest text-[var(--ml-text-secondary)] flex items-center gap-1.5"
      data-testid="engagement-log-live-indicator"
      title={`Auto-refreshes every 30s. Last refreshed ${new Date(lastFetchedAt).toLocaleTimeString()}.`}
    >
      <span
        className="w-1.5 h-1.5 rounded-full bg-[var(--ml-primary)] animate-pulse"
        aria-hidden="true"
      />
      Live · updated{" "}
      <span data-testid="engagement-log-updated-relative">
        {formatRelative(lastFetchedAt, Date.now())}
      </span>
    </span>
  );
}

export default function EngagementLog({ events, lastFetchedAt }) {
  return (
    <div className="ml-card p-5 mb-6" data-testid="engagement-log">
      <div className="mb-3 flex items-center justify-between gap-3">
        <div className="ml-mono text-[10px] uppercase tracking-widest text-[var(--ml-text-secondary)] flex items-center gap-1.5">
          <ClockCounterClockwise size={11} weight="bold" /> Engagement log
        </div>
        <LiveIndicator lastFetchedAt={lastFetchedAt} />
      </div>
      {events.length === 0 ? (
        <div
          className="text-sm text-[var(--ml-text-secondary)]"
          data-testid="engagement-log-empty"
        >
          No engagement events yet. They&apos;ll appear here as you copy share links,
          open client briefings in your email, and export PDFs.
        </div>
      ) : (
        <ol className="space-y-2" data-testid="engagement-log-list">
          {events.map((e) => (
            <EngagementRow key={e.id} event={e} />
          ))}
        </ol>
      )}
    </div>
  );
}

function EngagementRow({ event }) {
  const meta =
    EVENT_LABELS[event.type] || {
      label: event.type.replace(/_/g, " "),
      icon: ClockCounterClockwise,
    };
  const Icon = meta.icon;
  const hasMeta = event.metadata && Object.keys(event.metadata).length > 0;
  return (
    <li
      data-testid={`engagement-event-${event.type}`}
      className="flex items-start gap-3 text-sm border-b border-[var(--ml-border)] last:border-b-0 pb-2 last:pb-0"
    >
      <div className="w-7 h-7 bg-[var(--ml-surface)] border border-[var(--ml-border)] flex items-center justify-center shrink-0">
        <Icon size={13} weight="bold" color="#0f62fe" />
      </div>
      <div className="min-w-0 flex-1">
        <div className="font-medium">{meta.label}</div>
        <div className="ml-mono text-[10px] uppercase tracking-widest text-[var(--ml-text-secondary)]">
          {new Date(event.created_at).toLocaleString()}
          {hasMeta && <span> · {summarizeMetadata(event.metadata)}</span>}
        </div>
      </div>
    </li>
  );
}
